import { SegmentedControl } from 'blaze-ui/SegmentedControl/SegmentedControl'
import { useEffect, useState, useRef } from 'react'
import Styles from './SetupBasics.module.scss'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { MobileWizard } from 'components/Wizards/MobileWizards/Layout'
import { VisualSwitch } from 'components/VisualSwitch/VisualSwitch'
import { Pressable } from '@react-aria/interactions'
import { Button } from 'blaze-ui/Button'
import { ArrowRight } from 'blaze-ui/icons'
import { multiStep } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/multiStep'
import { StepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'
import { useField, useFormikContext } from 'formik'
import {
  ASPECT_RATIOS,
  AspectRatioType,
  DesignChannel,
  getAspectRatioKeyFromDesignChannel,
} from 'components/NewFileModal/Types'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import { capitalize } from 'lodash'
import { FilePathField } from '../../Fields/FilePathField'
import { Item } from '@react-stately/collections'
import { useImageWithBackgroundVariant } from 'components/Wizards/MobileWizards/hooks/useImageWithBackgroundVariant'
import cx from 'classnames'
import { useAutocomplete } from 'components/Wizards/DesktopWizards/useAutocomplete'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'

interface Props extends StepProps {
  selectedMedia: IMediaFile
  hideAspectRatio?: boolean
}

const TOP_BOTTOM_PADDING = 310

const getInitialAspectRatio = (width?: number, height?: number): AspectRatioType => {
  if (!width || !height) {
    return 'portrait'
  }

  if (width > height) {
    return 'landscape'
  } else if (width < height) {
    return 'portrait'
  } else {
    return 'square'
  }
}

export const SetupBasics = multiStep<Props>((props) => {
  const { schema, submit, selectedMedia, initialValues, getSchemaField, hideAspectRatio = false } = props
  const { meta } = schema.describe()
  const wizardSchema = useWizardSchema() as MultiStepSchema
  const { width, height } = initialValues
  const { setFieldValue, values, setStatus, initialStatus } = useFormikContext<any>()
  const initialAspectRatio = getInitialAspectRatio(width, height)
  const [type, setType] = useState<AspectRatioType>(initialAspectRatio)
  const [containerWidth, setContainerWidth] = useState<number | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const [removeBackground] = useField<boolean>('initial_media_file_remove_background')
  const channel = wizardSchema.meta.channel as DesignChannel
  const aspectRatios = wizardSchema.meta.aspectRatios || {}
  const aspectRatioTypes = Object.keys(aspectRatios) as AspectRatioType[]
  const { removedBackgroundVariant, imageUrl, videoUrl } = useImageWithBackgroundVariant(selectedMedia)
  const filenameField = getSchemaField('filename')

  const { handleGenerate, handleComplete, data } = useAutocomplete({
    schema: wizardSchema,
    values,
    setFieldValue,
    setStatus,
    initialStatus,
    onLoadingChange: setIsLoading,
  })

  const aspectRatioKey = aspectRatios[type] || getAspectRatioKeyFromDesignChannel(channel, 0, 0)
  const { width: selectedWidth, height: selectedHeight } = ASPECT_RATIOS[aspectRatioKey]
  const aspectRatio = selectedWidth / selectedHeight

  useEffect(() => {
    setFieldValue('width', selectedWidth)
    setFieldValue('height', selectedHeight)
  }, [selectedWidth, selectedHeight, setFieldValue])

  const hasAttemptedAutofill = useRef(false)

  useEffect(() => {
    if (!hasAttemptedAutofill.current) {
      hasAttemptedAutofill.current = true
      if (filenameField) {
        handleGenerate({ fields: ['filename'] })
      }
    }
  }, [filenameField, handleGenerate])

  const getContainerDimensions = () => {
    let width = undefined
    let height = undefined

    if (containerWidth) {
      width = containerWidth
      const availableHeight = window.innerHeight - TOP_BOTTOM_PADDING

      height = Math.min(width / aspectRatio, availableHeight)
      width = height * aspectRatio
    }

    return { width, height }
  }

  const handleSubmit = () => {
    submit()
  }

  const showAspectRatioControl = !hideAspectRatio && (aspectRatioTypes?.length ?? 0) > 1

  return (
    <>
      <PollFieldsetCompletion fieldsetCompletionId={data?.fieldset_completion.id} onComplete={handleComplete} />
      <MobileWizard.Content>
        <MobileWizard.Section>
          <div
            className={Styles.container}
            ref={(el) => {
              const width = el?.getBoundingClientRect()?.width
              if (width) {
                setContainerWidth(width)
              }
            }}
          >
            <MobileWizard.Title>{meta.title}</MobileWizard.Title>
            {showAspectRatioControl && (
              <div className={Styles.toggle}>
                <SegmentedControl
                  onItemClick={(name) => setType(name as AspectRatioType)}
                  activeItem={type}
                  className={Styles.toggleElement}
                  variant="secondary"
                  rounded
                  items={aspectRatioTypes.map((type) => ({
                    id: type,
                    name: capitalize(type),
                  }))}
                >
                  {(item) => <Item>{item.name}</Item>}
                </SegmentedControl>
              </div>
            )}
            <div className={Styles.previewContainerWrapper}>
              <div className={Styles.mediaPreviewContainer} style={{ ...getContainerDimensions() }}>
                {videoUrl && <video src={videoUrl} controls className={Styles.mediaPreview} />}
                {imageUrl && !videoUrl && (
                  <img
                    src={imageUrl}
                    alt="Selected media"
                    className={cx(Styles.mediaPreview, {
                      [Styles.backgroundRemoved]: removeBackground.value,
                    })}
                  />
                )}
              </div>
            </div>
            {!!removedBackgroundVariant && (
              <Pressable onPress={() => setFieldValue('initial_media_file_remove_background', !removeBackground.value)}>
                <div className={Styles.backgroundToggle}>
                  Remove background
                  <VisualSwitch variant="lg" active={removeBackground.value} />
                </div>
              </Pressable>
            )}
            {filenameField && (
              <FilePathField label="Name & Location" fileInputPlaceholder="Name your image..." isLoading={isLoading} />
            )}
          </div>
        </MobileWizard.Section>
      </MobileWizard.Content>
      <MobileWizard.ButtonFooter>
        <Button
          size="lg"
          variant="primary"
          endIcon={ArrowRight}
          fullWidth
          onPress={handleSubmit}
          isDisabled={isLoading}
        >
          Continue
        </Button>
      </MobileWizard.ButtonFooter>
    </>
  )
})
